import { useEffect, useState } from 'react';
import {
  Typography,
  Row,
  Col,
  Form,
  Select,
  Space,
  Alert,
  Modal,
  Input,
  Skeleton,
  Radio,
  Transfer,
  Table,
  Popconfirm,
  Empty,
} from 'antd';

import {
  CustomButton,
  CustomSelector,
  CustomTooltip,
} from '../../../components';

import AjaxTable from '../../../components/AjaxTable';
import {
  getIdentifierSelector,
  getIMInfo,
  getTermsSelector,
  massContractSearch,
  massContractSearchPayments,
  massContractSearchProducts,
} from '../../../api/module/mass_contract_modifications';
import { catchException } from '../../../utils/helpers/helpers';

const { Paragraph } = Typography;
const { TextArea } = Input;

const transferColumns = [
  {
    dataIndex: 'numero',
    title: 'Número de pago',
    render: (n) => `Pago ${n}`,
    // width: '20rem',
  },
  {
    dataIndex: 'valor',
    title: 'Valor',
    align: 'center',
    render: (v) =>
      `$ ${v
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`,
    // width: '3rem',
  },
];

const PaymentChoice = ({ value, onChange, ust }) => {
  const [op, setOp] = useState(false);

  const onSelfChange = (v) => {
    if (v.target.value === false) {
      onChange(undefined);
      setOp(true);
    } else {
      onChange(v.target.value);
    }
  };

  return (
    <Popconfirm
      open={op}
      placement="leftTop"
      okText="Entendido"
      title={
        ust ? (
          <>
            No permite la edición y eliminación de los productos existentes.
            <br />
            Únicamente permite la creación de nuevos productos.
          </>
        ) : (
          <>
            No permite la edición y eliminación de los productos y pagos
            existentes.
            <br />
            Únicamente permite la creación de nuevos productos y pagos.
          </>
        )
      }
      onConfirm={() => {
        onChange(false);
        setOp(false);
      }}
      showCancel={false}
    >
      <Radio.Group
        block
        options={[
          { label: 'Si', value: true },
          { label: 'No', value: false },
        ]}
        optionType="button"
        buttonStyle="solid"
        onChange={onSelfChange}
        value={value}
        disabled={op}
      />
    </Popconfirm>
  );
};

const PaymentsInput = ({ value, onChange, query }) => {
  const [dataSource, setDataSource] = useState();

  useEffect(() => {
    let isMounted = true;
    setDataSource();
    async function fetchData() {
      try {
        const res = await massContractSearchPayments(query);
        if (isMounted) setDataSource(res.data.results);
      } catch (error) {
        catchException(error);
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(query)]);

  if (!dataSource) {
    return <Skeleton loading active />;
  }

  return (
    <>
      <p>
        Seleccione los pagos a los cuales necesita hacerle modificaciones{' '}
        <CustomTooltip title="Aquí visualizará únicamente los pagos a los que puede hacer modificaciones (que no tengan aprobaciones de productos a la fecha) y la creación de nuevos productos y pagos" />
      </p>
      <Transfer
        targetKeys={value}
        showSearch
        showSelectAll={false}
        filterOption={(inputValue, item) =>
          `Pago ${item.numero} $ ${item.valor
            .toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1
        }
        rowKey={(record) => record.numero}
        onChange={onChange}
        dataSource={dataSource}
      >
        {({
          filteredItems,
          onItemSelect,
          onItemSelectAll,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
          direction,
        }) => {
          const rowSelection = {
            getCheckboxProps: () => ({
              disabled: listDisabled,
            }),
            onSelect({ numero }, selected) {
              onItemSelect(numero, selected);
            },
            onSelectAll(selected, _r, records) {
              onItemSelectAll(
                records.map((r) => r.numero),
                selected,
              );
            },
            selectedRowKeys: listSelectedKeys,
          };
          return (
            <Table
              rowKey={'numero'}
              rowSelection={rowSelection}
              dataSource={filteredItems}
              style={{ pointerEvents: listDisabled ? 'none' : undefined }}
              columns={transferColumns}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      direction === 'left'
                        ? value
                          ? 'Pagos/productos sin aprobación'
                          : 'No hay pagos/productos sin aprobación'
                        : 'Seleccione pagos/productos sin aprobación a modificar'
                    }
                  />
                ),
              }}
            />
          );
        }}
      </Transfer>
    </>
  );
};

const TermSelector = ({ value, onChange, cargo, setUst }) => {
  const onSelfChange = (value) => {
    setUst(value?.ust);
    onChange(value?.value);
  };

  return (
    <CustomSelector
      selectorProps={{
        style: { width: '100%' },
        placeholder:
          'Seleccione el identificador único del termino de referencia... ',
      }}
      endpointFetch={async () => {
        return await getTermsSelector(cargo);
      }}
      selectorKey={cargo}
      onChangeData={onSelfChange}
      initialValue={value}
    />
  );
};

const transferProductColumns = [
  {
    dataIndex: 'codigo',
    title: 'Número de producto',
    render: (n) => `Producto ${n}`,
  },
];

const ProductsInput = ({ value, onChange, query }) => {
  const [dataSource, setDataSource] = useState();

  useEffect(() => {
    let isMounted = true;
    setDataSource();
    async function fetchData() {
      try {
        const res = await massContractSearchProducts(query);
        if (isMounted) setDataSource(res.data.results);
      } catch (error) {
        catchException(error);
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [JSON.stringify(query)]);

  if (!dataSource) {
    return <Skeleton loading active />;
  }

  return (
    <>
      <p>
        Seleccione los productos a los cuales necesita hacerle modificaciones{' '}
        <CustomTooltip title="Aquí visualizará únicamente los productos a los que puede hacer modificaciones (que no tengan aprobaciones de productos a la fecha) y la creación de nuevos productos" />
      </p>
      <Transfer
        targetKeys={value}
        showSearch
        showSelectAll={false}
        filterOption={(inputValue, item) =>
          `Producto ${item.codigo}`
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1
        }
        rowKey={(record) => record.codigo}
        onChange={onChange}
        dataSource={dataSource}
      >
        {({
          filteredItems,
          onItemSelect,
          onItemSelectAll,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
          direction,
        }) => {
          const rowSelection = {
            getCheckboxProps: () => ({
              disabled: listDisabled,
            }),
            onSelect({ codigo }, selected) {
              onItemSelect(codigo, selected);
            },
            onSelectAll(selected, _r, records) {
              onItemSelectAll(
                records.map((r) => r.codigo),
                selected,
              );
            },
            selectedRowKeys: listSelectedKeys,
          };
          return (
            <Table
              rowKey={'codigo'}
              rowSelection={rowSelection}
              dataSource={filteredItems}
              style={{ pointerEvents: listDisabled ? 'none' : undefined }}
              columns={transferProductColumns}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      direction === 'left'
                        ? value
                          ? 'Productos sin aprobación'
                          : 'No hay productos sin aprobación'
                        : 'Seleccione productos sin aprobación a modificar'
                    }
                  />
                ),
              }}
            />
          );
        }}
      </Transfer>
    </>
  );
};

const MassContractSearch = ({ project_id, next, positions }) => {
  const [query, setQuery] = useState(undefined);
  const [queryCount, setQueryCount] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [form] = Form.useForm();

  const [nextForm] = Form.useForm();

  const [selected, setSelected] = useState([]);

  const [errorMassID, setErrorMassID] = useState(false);

  const [imInfo, setImInfo] = useState({ im: undefined, loading: false });

  const [viewRestricted, setViewRestricted] = useState(false);

  const [ust, setUst] = useState(undefined);

  const onChangeTerm = (termData) => {
    setUst(termData?.ust);
    form.setFieldsValue({
      termino: termData?.value,
      identificador_masivo: undefined,
      modifica_pagos: undefined,
      pagos: undefined,
      productos: undefined,
    });
  };

  const makeQuery = (data, pagos = false) => {
    delete data['modifica_pagos'];
    if (pagos === false) {
      delete data['pagos'];
      delete data['productos'];
    }
    const q = Object.entries(data || {}).reduce((pv, cv) => {
      if ((cv[0] === 'identificador_masivo') & (cv[1] === 1))
        pv.push(`filter[identificador_masivo__isnull]=true`);
      else if (cv[1]) pv.push(`filter[${cv[0]}]=${cv[1]}`);
      return pv;
    }, []);
    q.push(`filter[proyecto]=${project_id}`);
    return q.join('&');
  };

  const clearFetchIMInfo = async (im) => {
    if (im === undefined) {
      setImInfo({ im: undefined, loading: false });
    } else if (im !== imInfo.im) {
      setImInfo({
        im: im,
        loading: true,
      });
      const res = await getIMInfo(
        // Since im may not be set yet, it is added
        makeQuery({ ...form.getFieldsValue(), identificador_masivo: im }),
      );
      // In case im has already changed, the request should not change imInfo
      setImInfo((s) =>
        s.im === im ? { ...res.data, im: im, loading: false } : s,
      );
    }
  };

  const onFinish = async (data) => {
    setLoading(true);
    setSelected([]);
    setQuery(makeQuery(data, true));
    setQueryCount('...');
    setLoading(false);
  };

  const rowSelection = {
    fixed: true,
    preserveSelectedRowKeys: true,
    selectedRowKeys: selected,
    onChange: async (selectedRowKeys, _r, _info) => {
      setSelected(selectedRowKeys);
    },
  };

  const columns = [
    {
      title: 'Tipo Doc',
      dataIndex: 'tipo_identificacion',
      width: '6rem',
      align: 'center',
    },
    {
      title: 'Documento',
      dataIndex: 'numero_identificacion',
    },
    {
      title: 'Nombre Contratista/Proveedor',
      dataIndex: 'nombre_completo',
    },
    {
      title: 'No. Contrato',
      dataIndex: 'contrato_num',
      align: 'center',
    },
  ];

  const onNext = async (data) => {
    setLoadingNext(true);
    try {
      await next({
        ...data,
        contracts: selected,
        edita_existentes: form.getFieldValue('modifica_pagos'),
      });
    } catch (error) {
      setLoadingNext(false);
      if (error.isMassIDErr) setErrorMassID(true);
    }
  };

  return (
    <div>
      <Alert
        showIcon
        type="info"
        message="Información importante"
        description="Los resultados de la búsqueda mostrará los contratistas/proveedores que han aceptado contratación en el proyecto seleccionado con contrato VIGENTE o ACTIVO."
      />
      <br />
      <Paragraph>
        Seleccione el criterio por el cual desea realizar la modificación
        contractual masiva
      </Paragraph>
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        preserve={false}
        onValuesChange={(cv, av) => {
          let newIM = undefined;
          if ('cargo' in cv) {
            form.setFieldsValue({
              termino: undefined,
              identificador_masivo: undefined,
              modifica_pagos: undefined,
              pagos: undefined,
              productos: undefined,
            });
            setUst(undefined);
          } else if ('termino' in cv) {
            form.setFieldsValue({
              identificador_masivo: undefined,
              modifica_pagos: undefined,
              pagos: undefined,
              productos: undefined,
            });
          } else if ('identificador_masivo' in cv) {
            newIM = cv.identificador_masivo;
            form.setFieldsValue({
              modifica_pagos: undefined,
              pagos: undefined,
              productos: undefined,
            });
          } else if ('modifica_pagos' in cv) {
            newIM = av.identificador_masivo;
            form.setFieldsValue({
              pagos: undefined,
              productos: undefined,
            });
          } else if ('pagos' in cv || 'productos' in cv) {
            newIM = av.identificador_masivo;
          }
          setQuery(undefined);
          setSelected([]);
          setQueryCount(undefined);
          clearFetchIMInfo(newIM);
        }}
      >
        <Form.Item
          label="Cargo"
          name="cargo"
          rules={[{ required: true, message: 'Este campo es requerido' }]}
        >
          <Select
            placeholder="Seleccione un cargo/rol..."
            allowClear
            options={positions}
          />
        </Form.Item>
        <Form.Item noStyle dependencies={['cargo']}>
          {({ getFieldValue }) => {
            const cargo = getFieldValue('cargo');
            if (cargo) {
              return (
                <Form.Item
                  label="Identificador interno del TDR"
                  name="termino"
                  rules={[
                    { required: true, message: 'Este campo es requerido' },
                  ]}
                >
                  <TermSelector setUst={setUst} cargo={cargo} />
                </Form.Item>
              );
            } else return null;
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={['cargo', 'termino']}>
          {({ getFieldValue }) => {
            const termino = getFieldValue('termino');
            if (termino) {
              return (
                <Form.Item
                  label="Identificador interno de la Modificación masiva"
                  name="identificador_masivo"
                  tooltip={{
                    title:
                      'Seleccione el identificador interno de la modificación contractual a realizar según el listado de resultados en caso de requerirlo',
                  }}
                  rules={[
                    { required: true, message: 'Este campo es requerido' },
                  ]}
                >
                  <CustomSelector
                    selectorProps={{
                      style: { width: '100%' },
                      placeholder:
                        'Seleccione el identificador interno de la modificación masiva... ',
                    }}
                    endpointFetch={async () => {
                      return await getIdentifierSelector(termino);
                    }}
                    selectorKey={termino}
                  />
                </Form.Item>
              );
            } else return null;
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={['identificador_masivo']}>
          {({ getFieldValue }) => {
            const identificador_masivo = getFieldValue('identificador_masivo');
            if (identificador_masivo) {
              return (
                <>
                  {imInfo.loading || imInfo.im !== identificador_masivo ? (
                    <Skeleton loading active />
                  ) : (
                    <>
                      <Alert
                        description={
                          <>
                            <p>
                              Total de contratistas según la modificación masiva
                              seleccionada: {imInfo.total}
                            </p>
                            <p>
                              Contratistas en otros procesos:{' '}
                              {imInfo.restricted}{' '}
                              {imInfo.restricted > 0 ? (
                                <CustomButton
                                  text="Ver"
                                  htmlType="button"
                                  type="default"
                                  onClick={() => setViewRestricted(true)}
                                />
                              ) : null}
                            </p>
                            <p>
                              Contratistas disponibles para agregarlos a la
                              modificación masiva: {imInfo.available}
                            </p>
                          </>
                        }
                        type="success"
                      />

                      {imInfo.expired > 0 ? (
                        <>
                          <br />
                          <Alert
                            showIcon
                            description={
                              <b>
                                Contratistas con fecha de terminación expirada:{' '}
                                {imInfo.expired}
                              </b>
                            }
                            type="error"
                          />
                        </>
                      ) : null}
                      <br />
                      {imInfo?.available > 0 ? (
                        <Alert
                          type="info"
                          icon
                          message={
                            ust
                              ? '¿Esta modificación masiva implica editar y/o eliminar productos sin aprobación?'
                              : '¿Esta modificación masiva implica editar, eliminar y/o redistribuir pagos y/o productos sin aprobación?'
                          }
                          description={
                            <Form.Item
                              name={'modifica_pagos'}
                              rules={[
                                {
                                  required: true,
                                  message: 'Este campo es requerido',
                                },
                              ]}
                              style={{ maxWidth: '10rem' }}
                            >
                              <PaymentChoice ust={ust} />
                            </Form.Item>
                          }
                        />
                      ) : null}
                      <br />
                    </>
                  )}
                </>
              );
            } else return null;
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={['modifica_pagos']}>
          {({ getFieldsValue }) => {
            const queryData = getFieldsValue([
              'cargo',
              'termino',
              'identificador_masivo',
              'modifica_pagos',
            ]);
            if (queryData?.modifica_pagos) {
              const paymentQuery = makeQuery(queryData);
              if (ust) {
                return (
                  <Form.Item
                    name={'productos'}
                    rules={[
                      { required: true, message: 'Este campo es requerido' },
                    ]}
                  >
                    <ProductsInput query={paymentQuery} />
                  </Form.Item>
                );
              }
              return (
                <Form.Item
                  name={'pagos'}
                  rules={[
                    { required: true, message: 'Este campo es requerido' },
                  ]}
                >
                  <PaymentsInput query={paymentQuery} />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={['modifica_pagos', 'pagos']}>
          {({ getFieldsValue }) => {
            const { modifica_pagos, pagos, productos } = getFieldsValue([
              'modifica_pagos',
              'pagos',
              'productos',
            ]);
            if (
              (modifica_pagos &&
                (pagos?.length > 0 || productos?.length > 0)) ||
              modifica_pagos === false
            ) {
              return (
                <Row gutter={8} justify="end">
                  <Col>
                    <CustomButton loading={loading} text="Buscar" />
                  </Col>
                  <Col>
                    <CustomButton
                      htmlType="button"
                      type="default"
                      onClick={() => {
                        form.resetFields();
                        setQuery(undefined);
                        setSelected([]);
                        setUst(undefined);
                      }}
                      danger={true}
                      text="Borrar filtros"
                    />
                  </Col>
                </Row>
              );
            }
            return null;
          }}
        </Form.Item>
      </Form>
      <Modal
        open={viewRestricted}
        title="Contratistas en otros procesos"
        width={'80%'}
        footer={null}
        closable
        onCancel={() => setViewRestricted(false)}
      >
        <AjaxTable
          columns={[...columns, { title: 'Observaciones', dataIndex: 'obs' }]}
          tableTitle={'Listado de contratistas'}
          endpoint={massContractSearch}
          query={`${makeQuery(form.getFieldsValue())}&filter[restricted]=true`}
          searchInput
          exportCSV
        />
      </Modal>
      <br />
      {query && (
        <>
          {queryCount &&
          (query.includes('filter[pagos]') ||
            query.includes('filter[productos]')) ? (
            <Alert
              description={`Total de contratistas según ${
                ust ? 'productos' : 'pagos'
              } a modificar: ${queryCount}`}
              type="success"
            />
          ) : null}
          <AjaxTable
            columns={columns}
            tableTitle={
              'Seleccione los contratistas que modificará masivamente'
            }
            endpoint={massContractSearch}
            rowSelection={rowSelection}
            query={`${query}&filter[restricted]=false`}
            setCount={setQueryCount}
            searchInput
            exportCSV
            showSizeChanger
          />

          <Alert
            description={`Total de contratistas seleccionados para modificar: ${
              selected?.length || 0
            }`}
            type="success"
          />

          <br />
          <Form form={nextForm} onFinish={onNext}>
            <Form.Item
              name="identificador_masivo"
              label="Identificador interno de la modificación masiva"
              rules={[{ required: true }]}
            >
              <TextArea
                placeholder="Digite aquí"
                maxLength={80}
                showCount
                autoSize={{ minRows: 1, maxRows: 1 }}
              />
            </Form.Item>
          </Form>

          {errorMassID && (
            <>
              <Alert
                showIcon
                message="El identificador masivo ya existe."
                type="error"
              />
              <br />
            </>
          )}

          <Space style={{ justifyContent: 'space-evenly', width: '100%' }}>
            <CustomButton
              type="primary"
              text="Iniciar modificación"
              onClick={() => {
                if (selected.length < 2) {
                  Modal.error({
                    title: 'Error',
                    okText: 'Ok',
                    content:
                      'Recuerde que debe seleccionar dos o más personas para continuar, de lo contrario, seleccione la opción de "Nueva modificación contractual - Individual"',
                  });
                } else nextForm.submit();
              }}
              loading={loadingNext}
            />
            <CustomButton type="info" text="Otras acciones" />
          </Space>
        </>
      )}
    </div>
  );
};

export default MassContractSearch;
